import { useLoginInfo } from '@/components/LoginInfo/provider';
import { useCallback, useState } from 'react';

type UseLayoutPreferenceArgs<T extends string> = {
  storageKey: string;
  defaultLayout: T;
  validValues: readonly T[];
  isEnabled: boolean;
};

const isBrowser = typeof window !== 'undefined';

const readStoredLayout = <T extends string>(
  storageKey: string,
  defaultLayout: T,
  validValues: readonly T[]
): T => {
  if (!isBrowser) return defaultLayout;

  const stored = window.localStorage.getItem(storageKey);
  if (stored === null) {
    return defaultLayout;
  }
  const match = validValues.find((value) => value === stored);
  if (match !== undefined) {
    return match;
  }
  // Unexpected/corrupt value (e.g. stale format) — clear it.
  window.localStorage.removeItem(storageKey);
  return defaultLayout;
};

/**
 * Shared persistence for the 支払明細 / 仕訳 card-vs-table layout toggles
 * (NA-5531). Reads the last-used layout from localStorage on mount and
 * writes on every change, scoped per user (mirrors `useDrawerWidth`).
 *
 * The `isEnabled` clamp mirrors the pre-NA-5531 behavior: regardless of what
 * is stored, when the caller's flag is off the returned layout is always
 * `defaultLayout`. Writes still happen on `setLayout` so that re-enabling
 * the flag later in the same session picks the right value back up.
 */
export const useLayoutPreference = <T extends string>({
  storageKey,
  defaultLayout,
  validValues,
  isEnabled,
}: UseLayoutPreferenceArgs<T>) => {
  const { currentUser } = useLoginInfo();
  const userId = currentUser?.id;

  const [layout, setLayoutState] = useState<T>(() => {
    if (!userId) return defaultLayout;
    return readStoredLayout(
      `${storageKey}_${userId}`,
      defaultLayout,
      validValues
    );
  });

  const setLayout = useCallback(
    (next: T) => {
      setLayoutState(next);
      if (isBrowser && userId) {
        window.localStorage.setItem(`${storageKey}_${userId}`, next);
      }
    },
    [storageKey, userId]
  );

  return {
    layout: isEnabled ? layout : defaultLayout,
    setLayout,
  };
};
